import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { Shield, Loader2, AlertCircle } from 'lucide-react'
import { authApi } from '@/api/auth'
import { useAuthStore } from '@/store/authStore'

export function LoginPage() {
  const navigate = useNavigate()
  const { login } = useAuthStore()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const emailRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)

  const loginMutation = useMutation({
    mutationFn: (credentials: { email: string; password: string }) => authApi.login(credentials),
    onSuccess: (data) => {
      if (data.user.userType !== 'admin') {
        setError('관리자 계정만 로그인할 수 있습니다.')
        return
      }
      login(data.user, data.accessToken, data.refreshToken)
      navigate('/')
    },
    onError: () => {
      setError('이메일 또는 비밀번호가 올바르지 않습니다.')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    // DOM에서 직접 값 가져오기 (브라우저 자동완성 대응)
    const emailValue = emailRef.current?.value || email
    const passwordValue = passwordRef.current?.value || password
    loginMutation.mutate({ email: emailValue, password: passwordValue })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-500 mb-4">
            <Shield className="w-8 h-8 text-slate-900" />
          </div>
          <h1 className="text-3xl font-bold text-white">Quebon Admin</h1>
          <p className="text-slate-400 mt-2">관리자 대시보드에 로그인하세요</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="flex items-center gap-2 p-4 rounded-lg bg-red-50 text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            <div>
              <label className="label">이메일</label>
              <input
                ref={emailRef}
                type="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@quebon.com"
                autoComplete="username"
                required
              />
            </div>

            <div>
              <label className="label">비밀번호</label>
              <input
                ref={passwordRef}
                type="password"
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loginMutation.isPending}
              className="btn-primary btn-lg w-full"
            >
              {loginMutation.isPending ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  로그인 중...
                </>
              ) : (
                '로그인'
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-200 text-center">
            <p className="text-sm text-slate-500">
              일반 사용자는{' '}
              <a href="http://localhost:3000" className="text-amber-600 hover:underline">
                메인 사이트
              </a>
              를 이용해주세요
            </p>
          </div>
        </div>

        <p className="text-center text-slate-500 text-sm mt-8">
          © 2024 Quebon. All rights reserved.
        </p>
      </div>
    </div>
  )
}
